import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Box, Button, Flex, Table, TableProps } from '@mantine/core';
import { memo, ReactElement } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { SecurityRow } from './SecurityRow/SecurityRow';
import { useSecuritiesTableController } from './useSecuritiesTableController';
import { FormSingleSecurity, SecuritiesForm } from './useSecuritiesTableController.types';

interface ColumnDefinition {
	accessorKey: string;
	header: string;
	textAlign?: 'left' | 'center' | 'right';
}

interface CurrencyByCcyCodeMetadataDto {
	code: string;
	metadata: Record<string, string>;
}

interface EmptyTableProps {
	text: string;
}

const EmptyTable = ({ text }: EmptyTableProps): ReactElement => (
	<Flex align="center" justify="center" p="xl">
		<span className="text-dimmed">{text}</span>
	</Flex>
);

interface BasicMantineTableProps {
	children: React.ReactNode;
	columns: ColumnDefinition[];
}

const BasicMantineTable = ({ children, columns }: BasicMantineTableProps): ReactElement => (
	<StyledTable>
		<thead>
			<tr>
				{columns.map((column, index) => (
					<StyledTableHeader
						key={index}
						style={{ textAlign: column.textAlign || 'left' }}
					>
						{column.header}
					</StyledTableHeader>
				))}
			</tr>
		</thead>
		<tbody>{children}</tbody>
	</StyledTable>
);

const StyledTable = styled(Table)<TableProps>`
	th,
	td {
		border-color: ${({ theme }) => theme.v3.colors.gray[2]};
	}

	thead tr {
		border-bottom: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	}

	tbody tr {
		border-bottom: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	}
`;

const StyledTableHeader = styled.th`
	border-bottom: 1px solid ${({ theme }) => theme.v3.colors.gray[2]} !important;
	font-weight: 600;
	padding: 12px 16px;
`;

interface SecuritiesTableProps {
	className?: string;
	securitiesForm: UseFormReturn<SecuritiesForm, unknown>;
	showFairMarketValuePerUnitColumn?: boolean;
	showEmptyState?: boolean;
	resetEditOnDestroy?: boolean;
	showEditButton?: boolean;
	emptyState?: React.ReactNode;
	currency?: CurrencyByCcyCodeMetadataDto;
	onEditSecurity?: (security: FormSingleSecurity) => void;
	onAddSecurity?: () => void;
}

const SecuritiesTable = ({
	securitiesForm,
	className,
	showFairMarketValuePerUnitColumn,
	showEmptyState = true,
	resetEditOnDestroy,
	showEditButton = true,
	emptyState,
	onEditSecurity,
	onAddSecurity,
}: SecuritiesTableProps): ReactElement => {
	const { securitiesFields, onRemoveSecurityRow, onAddSecurityRow } =
		useSecuritiesTableController(securitiesForm);

	const tMipManage = useTranslationMipManage();

	const columns = () => {
		const baseColumns: ColumnDefinition[] = [
			{
				accessorKey: 'type',
				header: tMipManage('root.securities.table.header.type'),
			},
			{
				accessorKey: 'name',
				header: tMipManage('root.securities.table.header.name'),
			},
			{
				accessorKey: 'votesPerUnit',
				header: tMipManage('root.securities.table.header.votes_per_unit'),
				textAlign: 'center',
			},
			{
				accessorKey: 'nominalValue',
				header: tMipManage('root.securities.table.header.nominal_value'),
				textAlign: 'center',
			},
		];

		// Add fair market value column if enabled
		if (showFairMarketValuePerUnitColumn) {
			baseColumns.push({
				accessorKey: 'valuePerUnit',
				header: tMipManage('root.securities.table.header.value_per_unit'),
				textAlign: 'center',
			});
		}

		// Add vesting and actions columns
		baseColumns.push(
			{
				accessorKey: 'isSubjectToVesting',
				header: tMipManage('root.securities.table.header.subject_to_vesting'),
				textAlign: 'center',
			},
			{
				accessorKey: 'actions',
				header: '',
				textAlign: 'center',
			}
		);

		return baseColumns;
	};

	const handleAddSecurity = () => {
		if (onAddSecurity) {
			onAddSecurity();
		} else {
			onAddSecurityRow();
		}
	};

	return (
		<div className={className}>
			<FormProvider {...securitiesForm}>
				{securitiesFields.length > 0 ? (
					<BasicMantineTable columns={columns()}>
						{securitiesFields.map((field, index) => {
							return (
								<SecurityRow
									key={field.id}
									fieldIndex={index}
									onRemove={onRemoveSecurityRow(field.id)}
									resetEditOnDestroy={resetEditOnDestroy}
									showEditButton={showEditButton}
									onEditSecurity={onEditSecurity}
								/>
							);
						})}
					</BasicMantineTable>
				) : (
					showEmptyState &&
					(emptyState || (
						<EmptyTable text={tMipManage('root.securities.table.empty_state')} />
					))
				)}

				<Box mt="12px">
					<Button
						variant="subtle"
						onClick={handleAddSecurity}
						data-testid={TEST_ELEMENT_IDS.ADD_SECURITY_BUTTON}
						leftSection={<Icon variant="add" size="lg" />}
					>
						<span>{tMipManage('root.securities.table.add_security')}</span>
					</Button>
				</Box>
			</FormProvider>
		</div>
	);
};

const _SecuritiesTableExport = memo(SecuritiesTable);

export { _SecuritiesTableExport as SecuritiesTable };
